// IFCViewer — veřejná fasáda SDK. Toto je STABILNÍ veřejný kontrakt mezi IFC
// viewerem a hostitelskou aplikací (§35). Fasáda:
//   - skrývá interní knihovny (web-ifc / thatopen / three) — §24,
//   - pracuje s objekty přes { modelId, globalId } — §7/§8,
//   - vysílá události (event-driven) — §9/§18,
//   - podporuje více modelů (federace) — §6,
//   - umí serializovat/obnovit ViewerState — §15.
// Interní jádro (ViewerCore) je plně oddělené od UI, takže jde vytvořit víc
// instancí a interní knihovny lze vyměnit bez změny tohoto API.
import * as THREE from "three";
import * as OBC from "@thatopen/components";
import { ViewerCore } from "./viewer-core.js";
import { EventEmitter } from "./events.js";
import { ErrorCode, ViewerError, wrapError } from "./errors.js";
import { SDK_VERSION } from "./version.js";
import { buildIdIndex, isValidGlobalId, expressToGlobalId, globalIdToExpress, fragmentMapForExpress } from "./globalId.js";
import { extractProperties } from "./properties.js";
import { Sections } from "./sections.js";
import { StreamSupport, normalizeStreamBase } from "./stream.js";

const SELECT_STYLE = "sdk-selection";
const SELECT_COLOR = 0x2f7bf6;
const OUTLINE_COLOR = 0xff00ff;   // magenta zvýraznění hran vybraného prvku

// Inverzní mapa IFC třída (název) → číselný typ (pro find / rychlé dotazy).
const CLASS_TO_TYPE = (() => {
  const inv = {};
  try { for (const [code, name] of Object.entries(OBC.IfcCategoryMap)) inv[name] = Number(code); } catch (_) {}
  return inv;
})();

export class IFCViewer {
  constructor(options = {}) {
    if (typeof options === "string" || (options && options.nodeType)) options = { container: options };
    this._opts = Object.assign({
      theme: "dark",           // "light" | "dark" — barevné schéma vieweru
      grid: true,
      selectionEnabled: true,
      sectionsEnabled: true,
      language: "cs",
      navigation: "orbit",
      spaceOpacity: 0.5,       // neprůhlednost místností (IfcSpace), 0–1
    }, options);
    // `background` (hex / {r,g,b} / "light" / "dark") má přednost; jinak platí `theme`.
    if (this._opts.background == null) this._opts.background = this._opts.theme;

    this._events = new EventEmitter();
    this._core = new ViewerCore();
    this._models = new Map();     // modelId -> { modelId, group, name, visible, ids }
    this._modelSeq = 0;
    this._selection = [];         // [{ modelId, globalId, expressId, ifcClass }]
    this._hidden = new Set();     // "modelId|expressId"
    this._isolated = null;        // pole refs nebo null
    this._colorGroups = new Map();// colorHex -> Set("modelId|expressId")
    this._destroyed = false;
    this._initialized = false;
    this._programmatic = false;   // rozlišení klik vs. programový výběr
    this._stream = null;          // StreamSupport — vzniká s prvním streamovaným modelem
  }

  get version() { return SDK_VERSION; }

  // Aktuální barevné schéma ("light" | "dark").
  get theme() { return this._core ? this._core.theme : (this._opts.theme || "dark"); }

  // Přepne barevné schéma za běhu. Přijímá "light" | "dark" (i hex / {r,g,b}).
  setTheme(theme) {
    this._assertReady();
    this._core.setTheme(theme);
    this._emit("themeChanged", { theme: this._core.theme });
    return this;
  }

  // --- Inicializace ---------------------------------------------------------
  async init() {
    if (this._initialized) return this;
    const container = this._resolveContainer(this._opts.container);
    if (!container) throw new ViewerError(ErrorCode.INVALID_ARGUMENT, "IFCViewer: 'container' je povinný (HTMLElement nebo CSS selektor).");
    try {
      await this._core.init(container, {
        background: this._opts.background,
        grid: this._opts.grid !== false,
        wasm: this._opts.wasm || IFCViewer.defaultWasm,
      });
    } catch (e) {
      throw wrapError(ErrorCode.WEBGL_ERROR, "Inicializace vieweru selhala.", e);
    }
    if (this._opts.selectionEnabled !== false) this._setupSelection();
    this._setupCameraEvents();
    if (this._opts.sectionsEnabled !== false) this.sections = new Sections(this._core, (ev, p) => this._emit(ev, p));
    this._initialized = true;
    this._emit("viewerReady", { version: SDK_VERSION });
    return this;
  }

  _resolveContainer(c) {
    if (!c) return null;
    if (typeof c === "string") return document.querySelector(c);
    if (c.nodeType === 1) return c;
    return null;
  }

  // --- Načítání modelů (§5, §6) --------------------------------------------
  // source: File | Blob | ArrayBuffer | Uint8Array | { url, headers, loader, name } | string(url)
  async loadModel(source, opts = {}) {
    this._assertReady();
    const name = opts.name || (source && source.name) || null;
    const modelId = "m" + (++this._modelSeq);
    this._emit("modelLoading", { modelId, progress: 0 });
    let bytes;
    try {
      bytes = await this._toBytes(source, opts, modelId);
    } catch (e) {
      const err = e instanceof ViewerError ? e : wrapError(ErrorCode.IFC_LOAD_FAILED, "Načtení IFC selhalo.", e, { modelId });
      this._emit("error", err.toJSON());
      throw err;
    }
    this._emit("modelLoading", { modelId, progress: 0.6 });
    let group;
    try {
      group = await this._core.loadGroup(bytes, { spaceOpacity: this._opts.spaceOpacity });
    } catch (e) {
      const err = wrapError(ErrorCode.IFC_PARSE_FAILED, "Zpracování IFC selhalo.", e, { modelId });
      this._emit("error", err.toJSON());
      throw err;
    }
    const model = {
      modelId, group, visible: true,
      name: name || group.ifcMetadata && group.ifcMetadata.name || ("model " + this._modelSeq),
      ids: buildIdIndex(group),
      schema: group.ifcMetadata ? group.ifcMetadata.schema : null,
    };
    this._models.set(modelId, model);
    this._emit("modelLoading", { modelId, progress: 1 });
    this._emit("modelLoaded", { modelId, name: model.name, schema: model.schema, objectCount: model.ids.globalToExpress.size });
    if (this._models.size === 1) this.fitToModel();
    return { modelId, name: model.name };
  }

  async _toBytes(source, opts, modelId) {
    // Vlastní loader má přednost (autorizovaný zdroj, §5.4).
    if (opts.loader && typeof opts.loader === "function") {
      const r = await opts.loader({ modelId });
      return this._normalizeBytes(r);
    }
    if (typeof source === "string") return this._fetchBytes(source, opts.headers, modelId);
    if (source && typeof source === "object" && source.url) return this._fetchBytes(source.url, source.headers || opts.headers, modelId);
    if (source instanceof ArrayBuffer) return new Uint8Array(source);
    if (source instanceof Uint8Array) return source;
    if (typeof Blob !== "undefined" && source instanceof Blob) return new Uint8Array(await source.arrayBuffer());
    throw new ViewerError(ErrorCode.INVALID_ARGUMENT, "Nepodporovaný zdroj modelu.", { modelId });
  }
  _normalizeBytes(r) {
    if (r instanceof Uint8Array) return r;
    if (r instanceof ArrayBuffer) return new Uint8Array(r);
    if (typeof Blob !== "undefined" && r instanceof Blob) return r.arrayBuffer().then((b) => new Uint8Array(b));
    throw new ViewerError(ErrorCode.INVALID_ARGUMENT, "loader() musí vrátit ArrayBuffer/Uint8Array/Blob.");
  }
  async _fetchBytes(url, headers, modelId) {
    let resp;
    try { resp = await fetch(url, { headers: headers || undefined, cache: "no-store" }); }
    catch (e) { throw wrapError(ErrorCode.NETWORK_ERROR, "Síťová chyba při načítání IFC.", e, { modelId, url }); }
    if (!resp.ok) throw new ViewerError(ErrorCode.NETWORK_ERROR, "HTTP " + resp.status + " při načítání IFC.", { modelId, details: { status: resp.status, url } });
    return new Uint8Array(await resp.arrayBuffer());
  }

  // Streamovaný model z artefaktů ingestu (`<soubor>.ifc.stream/`). Geometrie se
  // dotahuje po dlaždicích podle pohledu kamery, takže klient IFC neparsuje a paměť
  // zůstává omezená i u velkých modelů. Identita prvků (GlobalId), podlaží a strom
  // pocházejí z light-indexu. source: bázová URL streamu | { base, ifcUrl?, name? }
  // (ifcUrl = zdrojový IFC pro doplnění vlastností ze serveru a pro nouzové načtení).
  async loadStream(source, opts = {}) {
    this._assertReady();
    const spec = typeof source === "string" ? { base: source } : (source || {});
    const base = normalizeStreamBase(spec.base);
    const modelId = "m" + (++this._modelSeq);
    this._emit("modelLoading", { modelId, progress: 0 });
    if (!this._stream) this._stream = new StreamSupport(this._core, this);
    let loaded;
    try {
      loaded = await this._stream.load(base);
    } catch (e) {
      const err = e instanceof ViewerError
        ? new ViewerError(e.code, e.message, { modelId, details: e.details, cause: e.cause })
        : wrapError(ErrorCode.IFC_LOAD_FAILED, "Načtení streamovaného modelu selhalo.", e, { modelId });
      this._emit("error", err.toJSON());
      throw err;
    }
    const model = {
      modelId, group: loaded.group, visible: true,
      name: opts.name || spec.name || this._streamName(spec.ifcUrl, base),
      ids: loaded.index.ids,
      schema: null,
      streamed: true,
      stream: {
        base,
        ifcUrl: spec.ifcUrl || null,
        settings: loaded.settings,
        light: loaded.light,
        index: loaded.index,
        box: loaded.box,
        propsAttach: null,
      },
    };
    this._models.set(modelId, model);
    this._stream.wirePicking();
    this._emit("modelLoading", { modelId, progress: 1 });
    this._emit("modelLoaded", { modelId, name: model.name, schema: null, objectCount: model.ids.globalToExpress.size, streamed: true });
    if (this._models.size === 1) this.fitToModel();
    return { modelId, name: model.name, streamed: true };
  }

  // Výchozí název streamovaného modelu: název souboru IFC, jinak adresář streamu.
  _streamName(ifcUrl, base) {
    const fileName = (url) => {
      try { return decodeURIComponent(new URL(url, base).pathname.split("/").filter(Boolean).pop() || ""); }
      catch (_) { return ""; }
    };
    return (ifcUrl && fileName(ifcUrl)) || fileName(base).replace(/\.stream$/, "") || ("model " + this._modelSeq);
  }

  removeModel(modelId) {
    const model = this._models.get(modelId);
    if (!model) throw new ViewerError(ErrorCode.MODEL_NOT_FOUND, "Model nenalezen: " + modelId, { modelId });
    this._selection = this._selection.filter((s) => s.modelId !== modelId);
    for (const key of Array.from(this._hidden)) if (key.startsWith(modelId + "|")) this._hidden.delete(key);
    this._core.disposeGroup(model.group);
    this._models.delete(modelId);
    if (model.streamed && this._stream) { void this._stream.colorsChanged(); void this._stream.selectionChanged(); }
    this._emit("modelRemoved", { modelId });
  }

  showModel(modelId) { this._setModelVisible(modelId, true); }
  hideModel(modelId) { this._setModelVisible(modelId, false); }
  _setModelVisible(modelId, visible) {
    const model = this._models.get(modelId);
    if (!model) throw new ViewerError(ErrorCode.MODEL_NOT_FOUND, "Model nenalezen: " + modelId, { modelId });
    model.visible = visible; model.group.visible = visible;
    if (model.streamed && this._stream) { this._stream.applyVisibility(model); this._stream.scheduleRefresh(); }
    this._emit("modelVisibilityChanged", { modelId, visible });
  }

  listModels() {
    return Array.from(this._models.values()).map((m) => ({ modelId: m.modelId, name: m.name, visible: m.visible, schema: m.schema, streamed: !!m.streamed }));
  }

  // Existuje prvek s daným GlobalId (v daném nebo libovolném modelu)? Synchronní,
  // pro převod/ověření referencí (BCF ComponentResolver, §9/§29).
  hasObject(modelId, globalId) {
    if (modelId) { const m = this._models.get(modelId); return !!(m && globalIdToExpress(m, globalId) != null); }
    for (const m of this._models.values()) if (globalIdToExpress(m, globalId) != null) return true;
    return false;
  }

  // --- Rozlišení referencí objektů -----------------------------------------
  // Přijímá: {modelId,globalId} | [refs] | {modelId, globalIds:[...]} .
  // Vrací pole { model, expressId, globalId }.
  _resolve(objects, { strict = false } = {}) {
    const list = [];
    const push = (modelId, globalId) => {
      const model = this._models.get(modelId);
      if (!model) { if (strict) throw new ViewerError(ErrorCode.MODEL_NOT_FOUND, "Model nenalezen: " + modelId, { modelId }); return; }
      if (!isValidGlobalId(globalId)) { if (strict) throw new ViewerError(ErrorCode.INVALID_GLOBAL_ID, "Neplatný GlobalId.", { modelId, details: { globalId } }); return; }
      const eid = globalIdToExpress(model, globalId);
      if (eid == null) { if (strict) throw new ViewerError(ErrorCode.OBJECT_NOT_FOUND, "Objekt nenalezen: " + globalId, { modelId, details: { globalId } }); return; }
      list.push({ model, expressId: eid, globalId });
    };
    const one = (o) => {
      if (!o) return;
      if (typeof o === "string") { // "modelId|globalId" nebo jen globalId (jediný model)
        if (o.includes("|")) { const [mid, gid] = o.split("|"); push(mid, gid); }
        else if (this._models.size === 1) push(this._firstModelId(), o);
        return;
      }
      if (Array.isArray(o.globalIds)) { for (const gid of o.globalIds) push(o.modelId, gid); return; }
      if (o.modelId && o.globalId) { push(o.modelId, o.globalId); return; }
      if (o.globalId && this._models.size === 1) { push(this._firstModelId(), o.globalId); return; }
    };
    if (Array.isArray(objects)) objects.forEach(one); else one(objects);
    return list;
  }
  _firstModelId() { return this._models.keys().next().value; }

  // Seskupí rozřešené položky po modelu do FragmentIdMap (pro Highlighter/Hider).
  _mapsByModel(resolved) {
    const byModel = new Map();
    for (const r of resolved) {
      if (!byModel.has(r.model.modelId)) byModel.set(r.model.modelId, { model: r.model, express: [] });
      byModel.get(r.model.modelId).express.push(r.expressId);
    }
    const out = [];
    for (const { model, express } of byModel.values()) out.push({ model, fragMap: fragmentMapForExpress(model, express) });
    return out;
  }

  // --- Výběr objektu (§8/§9) ------------------------------------------------
  _setupSelection() {
    const hl = this._core.highlighter;
    try { hl.add(SELECT_STYLE, new THREE.Color(SELECT_COLOR)); } catch (_) {}
    const sel = hl.events[hl.config.selectName];
    if (sel) {
      sel.onHighlight.add((map) => this._onPick(map));
      sel.onClear.add(() => { if (!this._programmatic) this._commitSelection([], { interactive: true }); });
    }
    if (this._core.container) {
      this._core.container.addEventListener("dblclick", () => {
        if (this._selection.length) this._emit("objectDoubleClicked", { ...this._selection[0] });
      });
    }
  }

  // Klik uživatele: z FragmentIdMap odvodíme (model, expressID) a GlobalId.
  _onPick(fragMap) {
    const resolved = this._resolveFragMap(fragMap);
    this._commitSelection(resolved, { interactive: !this._programmatic });
  }

  _resolveFragMap(fragMap) {
    const fragments = this._core.components.get(OBC.FragmentsManager);
    const out = [];
    for (const fragID in fragMap) {
      const frag = fragments.list.get(fragID);
      if (!frag) continue;
      const model = this._modelOfGroup(frag.group);
      if (!model) continue;
      for (const eid of fragMap[fragID]) {
        const globalId = expressToGlobalId(model, eid);
        if (!globalId) continue;
        out.push({ modelId: model.modelId, globalId, expressId: eid, ifcClass: this._ifcClassSync(model, eid) });
      }
    }
    return out;
  }
  _modelOfGroup(group) {
    for (const m of this._models.values()) if (m.group === group) return m;
    return null;
  }
  _ifcClassSync(model, expressId) {
    if (model && model.streamed) return model.stream.index.classOf.get(Number(expressId)) || null;
    try {
      const entry = model.group.data.get(expressId);
      const typeCode = entry && entry[1] && entry[1][1];
      if (typeCode != null && OBC.IfcCategoryMap[typeCode]) return OBC.IfcCategoryMap[typeCode];
    } catch (_) {}
    return null;
  }

  // Uloží výběr, obarví ho a vyšle události.
  _commitSelection(resolved, { interactive } = {}) {
    this._selection = resolved.map((r) => ({ modelId: r.modelId, globalId: r.globalId, expressId: r.expressId, ifcClass: r.ifcClass || this._ifcClassSync(this._models.get(r.modelId), r.expressId) }));
    this._applySelectionOutline();
    // Streamované prvky: dotáhnout jejich dlaždice, aby byl obrys výběru celý.
    if (this._stream) void this._stream.selectionChanged();
    this._emit("selectionChanged", { selection: this._selection.slice(), primary: this._selection[0] || null });
    if (interactive && this._selection[0]) this._emit("objectClicked", { ...this._selection[0] });
  }

  // Zvýrazní hrany vybraných prvků magenta (parita se standalone aplikací).
  _applySelectionOutline() {
    if (!this._selection.length) { this._core.clearSelectionOutline(); return; }
    const byModel = new Map();
    for (const s of this._selection) {
      const model = this._models.get(s.modelId);
      if (!model) continue;
      if (!byModel.has(model)) byModel.set(model, []);
      byModel.get(model).push(s.expressId);
    }
    const fragMaps = [];
    for (const [model, express] of byModel) fragMaps.push(fragmentMapForExpress(model, express));
    this._core.setSelectionOutline(fragMaps, OUTLINE_COLOR);
  }

  async select(objects, opts = {}) {
    this._assertReady();
    const resolved = this._resolve(objects, { strict: false });
    const maps = this._mapsByModel(resolved);
    const hl = this._core.highlighter;
    this._programmatic = true;
    try {
      hl.clear(hl.config.selectName);
      let first = true;
      for (const { model, fragMap } of maps) {
        if (model.streamed) continue;   // streamované prvky zvýrazňuje obrys výběru
        await hl.highlightByID(hl.config.selectName, fragMap, first, false);
        first = false;
      }
    } catch (_) { /* výběr je best-effort */ }
    finally { this._programmatic = false; }
    // Stav výběru nastavíme přímo (nezávisle na onHighlight, který u programového
    // výběru záměrně nevysílá objectClicked).
    this._commitSelection(resolved.map((r) => ({ modelId: r.model.modelId, globalId: r.globalId, expressId: r.expressId })), { interactive: false });

    if (opts.isolate) this.isolate(objects);
    if (opts.highlight === false) hl.clear(hl.config.selectName);
    if (opts.zoom) this.zoomTo(objects);
    if (opts.properties) {
      const primary = resolved[0];
      if (primary) return this.getProperties({ modelId: primary.model.modelId, globalId: primary.globalId });
    }
    return this._selection.slice();
  }

  selectByGlobalId(modelId, globalId, opts = {}) {
    return this.select({ modelId, globalId }, opts);
  }

  clearSelection() {
    this._assertReady();
    try { this._core.highlighter.clear(this._core.highlighter.config.selectName); } catch (_) {}
    this._commitSelection([], { interactive: false });
  }

  getSelection() { return this._selection.slice(); }

  // --- Zvýraznění a barvy (§12) --------------------------------------------
  highlight(objects, opts = {}) {
    this._assertReady();
    const color = opts.color || SELECT_COLOR;
    return this.setColor(objects, color);
  }

  async setColor(objects, color) {
    this._assertReady();
    const hex = this._normColor(color);
    const styleName = "sdk-color-" + hex;
    const hl = this._core.highlighter;
    if (!hl.colors.has(styleName)) { try { hl.add(styleName, new THREE.Color(parseInt(hex, 16))); } catch (_) {} }
    const resolved = this._resolve(objects);
    if (!this._colorGroups.has(hex)) this._colorGroups.set(hex, new Set());
    const set = this._colorGroups.get(hex);
    let streamed = false;
    for (const { fragMap, model } of this._mapsByModel(resolved)) {
      if (model.streamed) streamed = true;
      else { try { await hl.highlightByID(styleName, fragMap, false, false); } catch (_) {} }
      for (const r of resolved) if (r.model === model) set.add(model.modelId + "|" + r.expressId);
    }
    if (streamed && this._stream) await this._stream.colorsChanged();
    this._emit("visibilityChanged", { type: "color", color: "#" + hex });
    return true;
  }

  resetColors() {
    this._assertReady();
    const hl = this._core.highlighter;
    for (const hex of this._colorGroups.keys()) { try { hl.clear("sdk-color-" + hex); } catch (_) {} }
    this._colorGroups.clear();
    if (this._stream) void this._stream.colorsChanged();
    this._emit("visibilityChanged", { type: "color", color: null });
  }
  _normColor(c) {
    if (typeof c === "number") return c.toString(16).padStart(6, "0");
    if (typeof c === "string") return c.replace(/^#/, "").toLowerCase();
    return "ff0000";
  }

  // --- Viditelnost (§13) ----------------------------------------------------
  show(objects) { this._setVisible(objects, true); }
  hide(objects) { this._setVisible(objects, false); }
  _setVisible(objects, visible) {
    this._assertReady();
    const resolved = this._resolve(objects);
    for (const { model, fragMap } of this._mapsByModel(resolved)) {
      if (model.streamed) continue;
      try { this._core.hider.set(visible, fragMap); } catch (_) {}
    }
    for (const r of resolved) {
      const key = r.model.modelId + "|" + r.expressId;
      if (visible) this._hidden.delete(key); else this._hidden.add(key);
    }
    if (this._stream && resolved.some((r) => r.model.streamed)) this._stream.applyAllVisibility();
    this._emit("visibilityChanged", { type: visible ? "show" : "hide", count: resolved.length });
  }

  isolate(objects) {
    this._assertReady();
    const resolved = this._resolve(objects);
    try { this._core.hider.set(false); } catch (_) {}
    for (const { model, fragMap } of this._mapsByModel(resolved)) {
      if (model.streamed) continue;
      try { this._core.hider.set(true, fragMap); } catch (_) {}
    }
    this._isolated = resolved.map((r) => ({ modelId: r.model.modelId, globalId: r.globalId }));
    this._hidden.clear();
    if (this._stream) this._stream.applyAllVisibility();
    this._emit("visibilityChanged", { type: "isolate", count: resolved.length });
  }

  showAll() {
    this._assertReady();
    try { this._core.hider.set(true); } catch (_) {}
    this._hidden.clear();
    this._isolated = null;
    if (this._stream) this._stream.applyAllVisibility();
    this._emit("visibilityChanged", { type: "showAll" });
  }

  // --- Kamera (§14) ---------------------------------------------------------
  zoomTo(objects) {
    this._assertReady();
    const resolved = this._resolve(objects);
    const box = this._boxOfResolved(resolved);
    if (box) this._core.fitToBox(box, true);
    return !!box;
  }
  fitToModel() {
    this._assertReady();
    const box = this._boxOfModels(Array.from(this._models.values()).filter((m) => m.visible));
    if (box) this._core.fitToBox(box, true);
    return !!box;
  }
  // Ohraničení modelů ve scéně. Streamovaný model má bbox z culleru (nezávisí na tom,
  // které dlaždice jsou právě načtené); klasický z geometrie skupiny.
  _boxOfModels(models) {
    const box = new THREE.Box3(); let any = false;
    const classic = this._core.boxOfGroups(models.filter((m) => !m.streamed).map((m) => m.group));
    if (classic) { box.union(classic); any = true; }
    for (const m of models) {
      if (!m.streamed || m.visible === false) continue;
      if (!m.stream.box && this._stream) m.stream.box = this._stream.computeModelBox(m.group.uuid);
      const b = m.stream.box ? m.stream.box.clone() : new THREE.Box3().setFromObject(m.group);
      if (!b.isEmpty()) { box.union(b); any = true; }
    }
    return any ? box : null;
  }

  _boxOfResolved(resolved) {
    const box = new THREE.Box3(); let any = false;
    const w = new THREE.Vector3();
    const streamed = new Map();
    for (const r of resolved) {
      if (r.model.streamed) {
        if (!streamed.has(r.model)) streamed.set(r.model, []);
        streamed.get(r.model).push(r.expressId);
        continue;
      }
      try {
        const group = r.model.group;
        // getItemVertices vrací vrcholy v LOKÁLNÍM prostoru skupiny (aplikuje jen
        // instanční matici, ne group.matrixWorld). Skupina je posunutá o federační
        // offset, takže bez převodu do světa by kamera mířila mimo model.
        group.updateMatrixWorld(true);
        const verts = group.getItemVertices(r.expressId) || [];
        for (const v of verts) { w.copy(v).applyMatrix4(group.matrixWorld); box.expandByPoint(w); any = true; }
      } catch (_) {}
    }
    for (const [model, expressIds] of streamed) {
      const b = this._stream ? this._stream.boxForExpress(model, expressIds) : null;
      if (b) { box.union(b); any = true; }
    }
    return any ? box : null;
  }

  setView(name) {
    this._assertReady();
    const controls = this._core.world.camera.controls;
    if (!controls) return false;
    const box = this._boxOfModels(Array.from(this._models.values()).filter((m) => m.visible)) || new THREE.Box3(new THREE.Vector3(-1, -1, -1), new THREE.Vector3(1, 1, 1));
    const c = box.getCenter(new THREE.Vector3());
    const s = box.getSize(new THREE.Vector3());
    const d = Math.max(s.x, s.y, s.z) * 1.8 + 1;
    const dirs = {
      top: [0, 1, 0], bottom: [0, -1, 0], front: [0, 0, 1], back: [0, 0, -1],
      right: [1, 0, 0], left: [-1, 0, 0], iso: [1, 0.8, 1],
    };
    const dir = dirs[name] || dirs.iso;
    const len = Math.hypot(dir[0], dir[1], dir[2]);
    controls.setLookAt(
      c.x + (dir[0] / len) * d, c.y + (dir[1] / len) * d, c.z + (dir[2] / len) * d,
      c.x, c.y, c.z, true,
    );
    return true;
  }

  getCameraState() {
    this._assertReady();
    const controls = this._core.world.camera.controls;
    const p = new THREE.Vector3(), t = new THREE.Vector3();
    try { controls.getPosition(p); controls.getTarget(t); } catch (_) {}
    return { position: { x: p.x, y: p.y, z: p.z }, target: { x: t.x, y: t.y, z: t.z } };
  }
  setCameraState(state, animate = true) {
    this._assertReady();
    if (!state || !state.position || !state.target) throw new ViewerError(ErrorCode.INVALID_ARGUMENT, "Neplatný stav kamery.");
    const controls = this._core.world.camera.controls;
    const p = state.position, t = state.target;
    try { controls.setLookAt(p.x, p.y, p.z, t.x, t.y, t.z, animate); } catch (_) {}
  }
  // Snímek aktuálního pohledu jako PNG Blob (pro BCF snapshot apod.).
  async captureSnapshot() { this._assertReady(); return this._core.captureSnapshot(); }

  // Převod bodu mezi IFC (Z nahoru) a scénou vieweru — pro integrace (BCF viewpoint).
  sceneToIfc(point) { this._assertReady(); return this._core.sceneToIfc(new THREE.Vector3(point.x, point.y, point.z)); }
  ifcToScene(point) { this._assertReady(); const v = this._core.ifcToScene(point); return { x: v.x, y: v.y, z: v.z }; }

  // Obecné řezné roviny v IFC souřadnicích (pro BCF clipping).
  getClippingPlanes() { this._assertReady(); return this.sections ? this.sections.getPlanes() : []; }
  setClippingPlanes(planes) { this._assertReady(); if (this.sections) this.sections.setPlanes(planes); }

  _setupCameraEvents() {
    const controls = this._core.world && this._core.world.camera && this._core.world.camera.controls;
    if (controls && controls.addEventListener) {
      controls.addEventListener("rest", () => this._emit("cameraChanged", this.getCameraState()));
    }
  }

  // --- Vlastnosti (§10) -----------------------------------------------------
  async getProperties(object) {
    this._assertReady();
    const resolved = this._resolve(object, { strict: true });
    if (!resolved.length) throw new ViewerError(ErrorCode.OBJECT_NOT_FOUND, "Objekt nenalezen.");
    const r = resolved[0];
    if (r.model.streamed) {
      // Plné vlastnosti z properties indexu streamu; bez něj light-index (+ serverový endpoint).
      if (await this._stream.ensureProperties(r.model)) {
        try { const props = await extractProperties(this._core, r.model, r.expressId, r.globalId); if (props) return props; } catch (_) {}
      }
      return this._stream.fallbackProperties(r.model, r.expressId, r.globalId);
    }
    try { return await extractProperties(this._core, r.model, r.expressId, r.globalId); }
    catch (e) { throw wrapError(ErrorCode.OBJECT_NOT_FOUND, "Načtení vlastností selhalo.", e, { modelId: r.model.modelId }); }
  }

  // --- Vyhledávání (§11) ----------------------------------------------------
  async find(query = {}) {
    this._assertReady();
    const models = query.modelId ? [this._models.get(query.modelId)].filter(Boolean) : Array.from(this._models.values());
    const results = [];
    for (const model of models) {
      if (model.streamed) { await this._findStreamed(model, query, results); continue; }
      let candidateIds = null;
      if (query.globalId) {
        const eid = globalIdToExpress(model, query.globalId);
        candidateIds = eid == null ? [] : [eid];
      } else if (query.ifcClass && CLASS_TO_TYPE[query.ifcClass] != null) {
        try {
          const props = await model.group.getAllPropertiesOfType(CLASS_TO_TYPE[query.ifcClass]);
          candidateIds = props ? Object.keys(props).map(Number) : [];
        } catch (_) { candidateIds = []; }
      }
      if (candidateIds == null) candidateIds = model.group.getAllPropertiesIDs();

      for (const eid of candidateIds) {
        if (await this._matches(model, eid, query)) {
          const globalId = expressToGlobalId(model, eid);
          if (globalId) results.push({ modelId: model.modelId, globalId, expressId: eid, ifcClass: this._ifcClassSync(model, eid) });
        }
      }
    }
    return results;
  }

  // Vyhledávání ve streamovaném modelu: třída/název/GlobalId z light-indexu (bez
  // stahování vlastností); dotazy na ObjectType/PredefinedType/vlastnosti dotáhnou
  // properties index streamu, pokud ho ingest vytvořil.
  async _findStreamed(model, query, results) {
    let candidateIds = this._stream.findCandidates(model, query);
    const propsQuery = { objectType: query.objectType, predefinedType: query.predefinedType, property: query.property };
    if (query.objectType != null || query.predefinedType != null || query.property) {
      if (!(await this._stream.ensureProperties(model))) return;
      const filtered = [];
      for (const eid of candidateIds) if (await this._matches(model, eid, propsQuery)) filtered.push(eid);
      candidateIds = filtered;
    }
    for (const eid of candidateIds) {
      const globalId = expressToGlobalId(model, eid);
      if (globalId) results.push({ modelId: model.modelId, globalId, expressId: eid, ifcClass: this._ifcClassSync(model, eid) });
    }
  }

  async _matches(model, eid, query) {
    const needAttrs = query.name != null || query.objectType != null || query.predefinedType != null || query.ifcClass != null;
    let attrs = null;
    if (needAttrs) { attrs = await model.group.getProperties(eid); if (!attrs) return false; }
    if (query.ifcClass != null) { const cls = OBC.IfcCategoryMap[attrs.type]; if (cls !== query.ifcClass) return false; }
    if (query.name != null && this._attr(attrs.Name) !== query.name) return false;
    if (query.objectType != null && this._attr(attrs.ObjectType) !== query.objectType) return false;
    if (query.predefinedType != null && this._attr(attrs.PredefinedType) !== query.predefinedType) return false;
    if (query.property) {
      const ok = await this._matchProperty(model, eid, query.property);
      if (!ok) return false;
    }
    return true;
  }
  _attr(v) { return v && typeof v === "object" && "value" in v ? v.value : v; }
  async _matchProperty(model, eid, q) {
    try {
      const defIDs = this._core.indexer.getEntityRelations(model.group, eid, "IsDefinedBy") || [];
      for (const pid of defIDs) {
        const pset = await model.group.getProperties(pid);
        if (!pset) continue;
        if (q.pset && this._attr(pset.Name) !== q.pset) continue;
        const refs = pset.HasProperties || pset.Quantities || [];
        for (const ref of refs) {
          const prop = await model.group.getProperties(ref.value);
          if (!prop) continue;
          if (q.name != null && this._attr(prop.Name) !== q.name) continue;
          if (q.value === undefined) return true;
          const pv = this._attr(prop.NominalValue) ?? this._attr(prop.Value);
          if (pv === q.value) return true;
        }
      }
    } catch (_) {}
    return false;
  }

  // Rozsah viditelných modelů v IFC souřadnicích. Hostitelské UI jej používá
  // pro rozsahy řezných rovin, aniž by znalo interní THREE/OBC scénu.
  getBounds(modelId = null) {
    this._assertReady();
    const models = modelId
      ? [this._models.get(modelId)].filter(Boolean)
      : Array.from(this._models.values()).filter((m) => m.visible);
    if (modelId && !models.length) throw new ViewerError(ErrorCode.MODEL_NOT_FOUND, "Model nenalezen: " + modelId, { modelId });
    const box = this._boxOfModels(models);
    if (!box) return null;
    const off = this._core.federationOffset;
    return {
      min: { x: box.min.x - off.x, y: -(box.max.z - off.z), z: box.min.y - off.y },
      max: { x: box.max.x - off.x, y: -(box.min.z - off.z), z: box.max.y - off.y },
    };
  }

  // Podlaží a jejich geometrické prvky podle stejné prostorové klasifikace jako
  // standalone viewer. Funguje i pro IFC, kde přímý vztah ContainsElements není
  // v indexu dostupný, a agreguje stejnojmenná podlaží napříč federací.
  async _storeyEntriesForModel(model) {
    if (model.streamed) return this._stream.storeyEntries(model);
    const group = model.group;
    const storeyType = CLASS_TO_TYPE.IFCBUILDINGSTOREY;
    try { delete this._core.classifier.list.spatialStructures; } catch (_) {}
    try { await this._core.classifier.bySpatialStructure(group, { useProperties: true }); } catch (_) {}
    let storeys = null;
    try { storeys = storeyType != null ? await group.getAllPropertiesOfType(storeyType) : null; } catch (_) {}
    if (!storeys) return [];
    const fragmentIds = new Set((group.items || []).map((fragment) => fragment && fragment.id).filter(Boolean));
    const entries = [];
    for (const id of Object.keys(storeys)) {
      const attrs = storeys[id];
      const name = this._attr(attrs && attrs.Name) || this._attr(attrs && attrs.LongName) || ("Podlaží " + id);
      const elevationRaw = this._attr(attrs && attrs.Elevation);
      const elevation = elevationRaw == null || elevationRaw === "" ? null : Number(elevationRaw);
      let map = {};
      try { map = this._core.classifier.find({ spatialStructures: [name] }) || {}; } catch (_) {}
      const expressIds = new Set();
      for (const [fragmentId, ids] of Object.entries(map)) {
        if (!fragmentIds.has(fragmentId)) continue;
        for (const expressId of ids || []) expressIds.add(Number(expressId));
      }
      const objects = [];
      for (const expressId of expressIds) {
        const globalId = expressToGlobalId(model, expressId);
        if (globalId) objects.push({ modelId: model.modelId, globalId });
      }
      entries.push({
        modelId: model.modelId,
        expressId: Number(id),
        globalId: expressToGlobalId(model, Number(id)),
        name,
        elevation: Number.isFinite(elevation) ? elevation : null,
        objects,
        _expressIds: expressIds,
      });
    }
    return entries;
  }

  // --- IFC třídy (filtr viditelnosti) ---------------------------------------
  // Třídy prvků s vykreslitelnou geometrií a jejich počty. Prostorové kontejnery
  // (projekt, budova, podlaží) geometrii nemají, proto ve výsledku nejsou.
  getIfcClasses(modelId = null) {
    this._assertReady();
    const models = modelId
      ? [this._models.get(modelId)].filter(Boolean)
      : Array.from(this._models.values()).filter((m) => m.visible);
    if (modelId && !models.length) throw new ViewerError(ErrorCode.MODEL_NOT_FOUND, "Model nenalezen: " + modelId, { modelId });
    const counts = new Map();
    const add = (cls) => { if (cls) counts.set(cls, (counts.get(cls) || 0) + 1); };
    for (const model of models) {
      if (model.streamed) {
        for (const info of Object.values(model.stream.light.elements || {})) add(info && info.ifcClass ? String(info.ifcClass).toUpperCase() : null);
        continue;
      }
      // group.data: expressID → [klíče fragmentů, [úroveň, kód třídy]]; bez fragmentů = bez geometrie.
      for (const [, entry] of model.group.data || []) {
        if (!entry || !entry[0] || !entry[0].length) continue;
        const typeCode = entry[1] && entry[1][1];
        add(typeCode != null ? OBC.IfcCategoryMap[typeCode] : null);
      }
    }
    return Array.from(counts, ([ifcClass, count]) => ({ ifcClass, count }))
      .sort((a, b) => a.ifcClass.localeCompare(b.ifcClass));
  }

  async getStoreys(modelId = null) {
    this._assertReady();
    const models = modelId
      ? [this._models.get(modelId)].filter(Boolean)
      : Array.from(this._models.values()).filter((m) => m.visible);
    if (modelId && !models.length) throw new ViewerError(ErrorCode.MODEL_NOT_FOUND, "Model nenalezen: " + modelId, { modelId });
    const byName = new Map();
    for (const model of models) {
      for (const entry of await this._storeyEntriesForModel(model)) {
        if (!byName.has(entry.name)) byName.set(entry.name, { name: entry.name, elevation: entry.elevation, objects: [] });
        const aggregate = byName.get(entry.name);
        if (aggregate.elevation == null && entry.elevation != null) aggregate.elevation = entry.elevation;
        aggregate.objects.push(...entry.objects);
      }
    }
    return Array.from(byName.values()).map((entry) => {
      const unique = new Map(entry.objects.map((ref) => [ref.modelId + "|" + ref.globalId, ref]));
      return { ...entry, objects: Array.from(unique.values()) };
    }).filter((entry) => entry.objects.length).sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
  }

  // --- Strom modelu (§17) ---------------------------------------------------
  // mode: containment (Projekt→Budova→Podlaží→prvky), component (jen
  // dekompozice/agregace) nebo floor (Podlaží→obsažené prvky).
  async getSpatialTree(modelId, options = {}) {
    this._assertReady();
    const model = modelId ? this._models.get(modelId) : this._models.values().next().value;
    if (!model) throw new ViewerError(ErrorCode.MODEL_NOT_FOUND, "Model nenalezen: " + modelId, { modelId });
    const group = model.group;
    const mode = options && ["containment", "component", "floor"].includes(options.mode) ? options.mode : "containment";
    if (model.streamed) return this._stream.spatialTree(model, mode);
    const projType = CLASS_TO_TYPE.IFCPROJECT;
    const nodeFor = async (eid) => {
      const attrs = await group.getProperties(eid);
      const elevationRaw = this._attr(attrs && attrs.Elevation);
      const elevation = elevationRaw == null || elevationRaw === "" ? null : Number(elevationRaw);
      return {
        modelId: model.modelId,
        expressId: eid,
        globalId: expressToGlobalId(model, eid),
        ifcClass: attrs ? OBC.IfcCategoryMap[attrs.type] : this._ifcClassSync(model, eid),
        name: this._attr(attrs && attrs.Name) || this._attr(attrs && attrs.LongName) || null,
        elevation: Number.isFinite(elevation) ? elevation : null,
        children: [],
      };
    };
    const build = async (eid, relations, visited) => {
      if (visited.has(eid)) return null;
      visited.add(eid);
      const node = await nodeFor(eid);
      for (const rel of relations) {
        let ids = [];
        try { ids = this._core.indexer.getEntityRelations(group, eid, rel) || []; } catch (_) {}
        for (const cid of ids) { const child = await build(cid, relations, visited); if (child) node.children.push(child); }
      }
      return node;
    };

    if (mode === "floor") {
      const roots = [];
      for (const entry of await this._storeyEntriesForModel(model)) {
        const node = await nodeFor(entry.expressId);
        for (const expressId of entry._expressIds) {
          if (expressId !== entry.expressId) node.children.push(await nodeFor(expressId));
        }
        roots.push(node);
      }
      roots.sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
      return roots;
    }

    let projects = null;
    try { projects = projType != null ? await group.getAllPropertiesOfType(projType) : null; } catch (_) {}
    if (!projects) return null;
    const relations = mode === "component" ? ["IsDecomposedBy"] : ["IsDecomposedBy", "ContainsElements"];
    const roots = [];
    const visited = new Set();
    for (const id of Object.keys(projects)) {
      const root = await build(Number(id), relations, visited);
      if (root) roots.push(root);
    }
    return roots.length === 1 ? roots[0] : roots;
  }

  // --- ViewerState (§15) ----------------------------------------------------
  getState() {
    this._assertReady();
    const colors = {};
    for (const [hex, set] of this._colorGroups) {
      colors["#" + hex] = Array.from(set).map((k) => { const [modelId, eid] = k.split("|"); const m = this._models.get(modelId); return { modelId, globalId: m ? expressToGlobalId(m, Number(eid)) : null }; }).filter((r) => r.globalId);
    }
    return {
      version: SDK_VERSION,
      camera: this.getCameraState(),
      models: this.listModels(),
      selection: this._selection.map((s) => ({ modelId: s.modelId, globalId: s.globalId })),
      hiddenObjects: Array.from(this._hidden).map((k) => { const [modelId, eid] = k.split("|"); const m = this._models.get(modelId); return { modelId, globalId: m ? expressToGlobalId(m, Number(eid)) : null }; }).filter((r) => r.globalId),
      isolatedObjects: this._isolated ? this._isolated.slice() : [],
      sectionPlanes: [],
      colors,
    };
  }
  async setState(state) {
    this._assertReady();
    if (!state) return;
    this.showAll();
    this.resetColors();
    this.clearSelection();
    if (Array.isArray(state.models)) for (const m of state.models) { const model = this._models.get(m.modelId); if (model) this._setModelVisible(m.modelId, m.visible !== false); }
    if (state.isolatedObjects && state.isolatedObjects.length) this.isolate(state.isolatedObjects);
    else if (state.hiddenObjects && state.hiddenObjects.length) this.hide(state.hiddenObjects);
    if (state.colors) for (const [hex, refs] of Object.entries(state.colors)) await this.setColor(refs, hex);
    if (state.selection && state.selection.length) await this.select(state.selection);
    if (state.camera) this.setCameraState(state.camera, false);
  }

  // --- Události (§18) -------------------------------------------------------
  on(event, handler) { return this._events.on(event, handler); }
  once(event, handler) { return this._events.once(event, handler); }
  off(event, handler) { this._events.off(event, handler); }
  _emit(event, payload) { this._events.emit(event, payload); }

  // --- Životní cyklus -------------------------------------------------------
  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    for (const modelId of Array.from(this._models.keys())) { try { this._core.disposeGroup(this._models.get(modelId).group); } catch (_) {} }
    this._models.clear();
    try { if (this._stream) this._stream.dispose(); } catch (_) {}
    this._stream = null;
    try { this._core.dispose(); } catch (_) {}
    this._events.clear();
    this._initialized = false;
  }

  _assertReady() {
    if (this._destroyed) throw new ViewerError(ErrorCode.DESTROYED, "Viewer byl zrušen (destroy).");
    if (!this._initialized) throw new ViewerError(ErrorCode.NOT_INITIALIZED, "Viewer není inicializován — zavolejte await viewer.init().");
  }
}
